import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { GoogleLogin } from '@react-oauth/google';

export default function Login() {
  const { theme } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, googleLogin } = useContext(AppContext);
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    const result = await googleLogin(credentialResponse.credential);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  const handleGoogleError = () => {
    setError("Google Login failed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError("Please fill all fields.");
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col justify-center bg-white dark:bg-black text-slate-900 dark:text-slate-200 px-4 py-6 sm:py-12 overflow-y-auto">
      <div className="w-full max-w-md mx-auto p-6 sm:p-8 sm:border border-slate-100 dark:border-slate-900/50 rounded-3xl my-auto">
        <div className="flex justify-center mb-5">
          <img src={theme === 'dark' ? "/logo.png" : "/logo-light.png"} alt="Selffo Logo" className="w-14 h-14 rounded-2xl shadow-lg" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-slate-900 dark:text-white text-center">
          Welcome Back 👀
        </h2>
        <p className="text-slate-500 mb-6 font-medium text-center text-sm sm:text-base">Spill the tea. Anonymously.</p>

        {error && <div className="p-3 mb-4 bg-red-500/10 border border-red-500/50 text-red-500 text-sm rounded-xl">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-500 mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-transparent focus:border-slate-300 dark:focus:border-slate-700 outline-none transition-all"
              placeholder="Your anonymous alias"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-500 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-transparent focus:border-slate-300 dark:focus:border-slate-700 outline-none transition-all"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 mt-2 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-200 text-white dark:text-slate-900 font-bold rounded-xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-4">
          <div className="h-px bg-slate-200 dark:bg-slate-800 flex-1"></div>
          <span className="text-sm font-semibold text-slate-400">OR</span>
          <div className="h-px bg-slate-200 dark:bg-slate-800 flex-1"></div>
        </div>

        <div className="mt-6 flex justify-center">
          {!process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
            <button
              type="button"
              onClick={() => setError("Google Login is currently being set up. Please use standard email login for now.")}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-800 rounded-full hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Sign in with Google</span>
            </button>
          ) : (
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme={theme === 'dark' ? 'filled_black' : 'outline'}
              text="continue_with"
              shape="pill"
            />
          )}
        </div>

        <p className="text-center text-slate-500 mt-6 text-sm">
          Don't have an account? <Link to="/signup" className="text-fuchsia-400 hover:text-fuchsia-300 font-semibold">Join now.</Link>
        </p>
      </div>
    </div>
  );
}
